import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom";

interface FlowContextInterface {
  currentStep: number;
  setCurrentStep: (step: number) => void;
  contractId: string | undefined;
  setContractId: (contractId: string | undefined) => void;
}

const useValue = (): FlowContextInterface => {
  const navigate = useNavigate();

  const initialContractId = (() => {
    let id = sessionStorage.getItem("contract_id");
    const flow = JSON.parse(sessionStorage.getItem("flow") ?? "{}");

    if (!id && Object.keys(flow).length) {
      id = Object.keys(flow)[0];
      sessionStorage.setItem("contract_id", id);
    }

    return id ?? undefined;
  })();

  const [contractId, setContractId] = useState<string | undefined>(initialContractId);

  // Keep sessionStorage in sync whenever contractId changes
  useEffect(() => {
    if (contractId) sessionStorage.setItem("contract_id", contractId);
  }, [contractId]);

  // Redirect if no contractId
  useEffect(() => {
    if (!contractId) navigate("/login");
  }, [contractId, navigate]);

  const [currentStep, setCurrentStep] = useState<number>(() => {
    const flow = JSON.parse(sessionStorage.getItem("flow") ?? "{}");
    return !contractId || !flow[contractId] ? 0 : (flow[contractId].currentStep ?? 0);
  });

  // Persist step
  useEffect(() => {
    if (!contractId) return;

    const flow = JSON.parse(sessionStorage.getItem("flow") ?? "{}");
    flow[contractId] = { currentStep };
    sessionStorage.setItem("flow", JSON.stringify(flow));
  }, [contractId, currentStep]);

  return {
    currentStep,
    setCurrentStep,
    contractId,
    setContractId,
  };
};

const FlowContext = createContext({} as ReturnType<typeof useValue>);

export const useFlow = () => useContext(FlowContext);

interface FlowProviderProps {
  children: ReactNode;
}

export const FlowContextProvider: React.FC<FlowProviderProps> = ({ children }) => {
  return (
    <FlowContext.Provider value={useValue()}>
      {children}
    </FlowContext.Provider>
  );
};